import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Trophy, 
  Star, 
  Medal,
  Crown,
  Target,
  TrendingUp,
  MapPin,
  Users,
  Loader2,
  ArrowLeft,
  Sparkles
} from "lucide-react";

// Türkiye illeri
const CITIES = [
  "Adana", "Ankara", "Antalya", "Bursa", "Denizli", "Diyarbakır", "Eskişehir",
  "Gaziantep", "İstanbul", "İzmir", "Kayseri", "Kocaeli", "Konya", "Mersin",
  "Sakarya", "Samsun", "Trabzon"
];

const RankingsPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("popularity");
  const [scope, setScope] = useState("country");
  const [scopeValue, setScopeValue] = useState("");
  const [rankings, setRankings] = useState([]);
  const [myRank, setMyRank] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRankings();
  }, [activeTab, scope, scopeValue]);

  const fetchRankings = async () => {
    setLoading(true);
    try {
      const endpoint = activeTab === "popularity" ? "/rankings/popularity" : "/rankings/goals";
      const params = new URLSearchParams({ scope, limit: "100" });
      if (scopeValue) params.append("scope_value", scopeValue);
      
      const response = await axios.get(`${API}${endpoint}?${params.toString()}`);
      setRankings(response.data.rankings || []);
      setMyRank(response.data.myRank);
    } catch (error) {
      console.error("Error fetching rankings:", error);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Crown className="h-6 w-6 text-amber-500" />;
      case 2:
        return <Medal className="h-6 w-6 text-gray-400" />;
      case 3:
        return <Medal className="h-6 w-6 text-amber-700" />;
      default:
        return <span className="w-6 text-center font-bold text-gray-500">{rank}</span>;
    }
  };

  const getRankBadge = (rank, city) => {
    if (rank === 1) {
      return `${city || "Türkiye"}'nin 1 Numarası 🥇`;
    } else if (rank <= 3) {
      return `${city || "Türkiye"} Top 3 🏆`;
    } else if (rank <= 10) {
      return `${city || "Türkiye"} Top 10 ⭐`;
    }
    return null;
  };

  const getScopeLabel = () => {
    if (scope === "country") return "Türkiye";
    if (scope === "city" && scopeValue) return scopeValue;
    return "Tüm Türkiye";
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg transition-colors duration-300">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Başlık */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2 dark:text-white">
              <Trophy className="h-6 w-6 text-amber-500" />
              Sıralamalar
            </h1>
            <p className="text-gray-500 dark:text-gray-400">En popüler oyuncular ve gol kralları</p>
          </div>
        </div>

        {/* Kullanıcının Sırası */}
        {myRank && (
          <Card className="mb-6 bg-gradient-to-r from-emerald-500 to-green-600 text-white">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="bg-white/20 rounded-full p-3">
                    <Star className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-emerald-100 text-sm">Senin Sıran</p>
                    <p className="text-3xl font-bold">#{myRank}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-emerald-100 text-sm">Popülerlik Puanı</p>
                  <p className="text-2xl font-bold">{user?.popularityPoints || 0}</p>
                </div>
              </div>
              {getRankBadge(myRank, user?.city) && (
                <Badge className="mt-3 bg-white/20 text-white">
                  {getRankBadge(myRank, user?.city)}
                </Badge>
              )}
            </CardContent>
          </Card>
        )}

        {/* Filtreler */}
        <Card className="mb-6">
          <CardContent className="py-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-[200px]">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Kapsam</label>
                <Select value={scope} onValueChange={(v) => { setScope(v); setScopeValue(""); }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Kapsam seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="country">🇹🇷 Tüm Türkiye</SelectItem>
                    <SelectItem value="city">🏙️ Şehir</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {scope === "city" && (
                <div className="flex-1 min-w-[200px]">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Şehir</label>
                  <Select value={scopeValue} onValueChange={setScopeValue}>
                    <SelectTrigger>
                      <SelectValue placeholder="Şehir seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {CITIES.map((city) => (
                        <SelectItem key={city} value={city}>{city}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Sıralama Tabları */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="popularity" className="gap-2">
              <Star className="h-4 w-4" />
              Popülerlik
            </TabsTrigger>
            <TabsTrigger value="goals" className="gap-2">
              <Target className="h-4 w-4" />
              Gol Kralları
            </TabsTrigger>
          </TabsList>

          <TabsContent value="popularity">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-amber-500" />
                  Popülerlik Sıralaması
                </CardTitle>
                <CardDescription>
                  {getScopeLabel()} - En çok sticker alan oyuncular
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
                  </div>
                ) : rankings.length === 0 ? (
                  <div className="text-center py-8">
                    <Star className="h-12 w-12 mx-auto text-gray-300 dark:text-gray-600 mb-2" />
                    <p className="text-gray-500 dark:text-gray-400">Henüz sıralama verisi yok</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {rankings.map((player, idx) => (
                      <div
                        key={player.id}
                        onClick={() => navigate(`/profile/${player.id}`)}
                        className={`flex items-center gap-4 p-3 rounded-lg cursor-pointer transition-colors ${
                          player.id === user?.id 
                            ? 'bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-700' 
                            : 'hover:bg-gray-50 dark:hover:bg-dark-card'
                        } ${player.rank <= 3 ? 'bg-amber-50 dark:bg-amber-900/20' : ''}`}
                      >
                        <div className="w-8 flex justify-center">
                          {getRankIcon(player.rank)}
                        </div>
                        <Avatar className="h-12 w-12">
                          <AvatarImage src={player.profileImage} />
                          <AvatarFallback className="bg-emerald-100 text-emerald-700">
                            {player.name?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate dark:text-white">{player.name}</p>
                          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                            {player.city && (
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {player.city}
                              </span>
                            )}
                            {player.clubName && (
                              <span className="flex items-center gap-1">
                                <Users className="h-3 w-3" />
                                {player.clubName}
                              </span>
                            )}
                          </div>
                          {player.rankTitle && (
                            <Badge variant="outline" className="mt-1 text-xs">
                              {player.rankTitle}
                            </Badge>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-1 text-amber-600 dark:text-amber-400 font-bold">
                            <Star className="h-4 w-4 fill-amber-400" />
                            {player.popularityPoints}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="goals">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-emerald-600" />
                  Gol Kralları
                </CardTitle>
                <CardDescription>
                  {getScopeLabel()} - En çok gol atan oyuncular
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
                  </div>
                ) : rankings.length === 0 ? (
                  <div className="text-center py-8">
                    <Target className="h-12 w-12 mx-auto text-gray-300 dark:text-gray-600 mb-2" />
                    <p className="text-gray-500 dark:text-gray-400">Henüz gol verisi yok</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {rankings.map((player) => (
                      <div
                        key={player.id}
                        onClick={() => navigate(`/profile/${player.id}`)}
                        className={`flex items-center gap-4 p-3 rounded-lg cursor-pointer transition-colors ${
                          player.id === user?.id 
                            ? 'bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-700' 
                            : 'hover:bg-gray-50 dark:hover:bg-dark-card'
                        } ${player.rank <= 3 ? 'bg-amber-50 dark:bg-amber-900/20' : ''}`}
                      >
                        <div className="w-8 flex justify-center">
                          {getRankIcon(player.rank)}
                        </div>
                        <Avatar className="h-12 w-12">
                          <AvatarImage src={player.profileImage} />
                          <AvatarFallback className="bg-emerald-100 text-emerald-700">
                            {player.name?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate dark:text-white">{player.name}</p>
                          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                            {player.city && (
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {player.city}
                              </span>
                            )}
                            {player.clubName && (
                              <span className="flex items-center gap-1">
                                <Users className="h-3 w-3" />
                                {player.clubName}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-3">
                            <div className="text-center">
                              <p className="text-xs text-gray-500 dark:text-gray-400">Gol</p>
                              <p className="font-bold text-emerald-600 dark:text-emerald-400">{player.totalGoals}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-xs text-gray-500 dark:text-gray-400">Asist</p>
                              <p className="font-bold text-blue-600 dark:text-blue-400">{player.totalAssists || 0}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-xs text-gray-500 dark:text-gray-400">Maç</p>
                              <p className="font-bold text-gray-600 dark:text-gray-300">{player.totalMatches || 0}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default RankingsPage;
